import { browserFetch } from '../../services/browserSession';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  BarChart3,
  Eye,
  Users,
  Heart,
  MessageCircle,
  Bookmark,
  ChevronDown,
  Instagram,
  Video,
  Image,
  Layers,
  Link as LinkIcon,
  Loader2,
  ExternalLink,
  Trophy,
  CheckCircle2,
  Clock3,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import { Card, CardContent } from '../common/design-system/Card';
import styles from './BusinessMetricsDashboard.module.scss';
import clsx from 'clsx';
import { refreshLatestCollabMetrics } from '../../services/api';
import MetricInfoPopover from '../common/MetricInfoPopover';

const luxuryEase = [0.23, 1, 0.32, 1];
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const itemVariants = { hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: luxuryEase } } };

const getPostTypeIcon = (postType) => {
    switch (postType?.toLowerCase()) {
        case 'reel': return <Video size={14} />;
        case 'story': return <Layers size={14} />;
        case 'post': return <Image size={14} />;
        default: return <LinkIcon size={14} />;
    }
};

const normalizeUrl = (url) => {
    if (!url) return '#';
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url.replace(/^\/+/,'')}`;
};

const metricNumber = (value) => Number(value || 0);

const formatNumber = (value, locale) => value === null || value === undefined ? '—' : metricNumber(value).toLocaleString(locale);

const formatPercent = (value) => value === null || value === undefined ? '—' : `${metricNumber(value).toFixed(1)}%`;

const formatDate = (value, locale, fallback) => {
    if (!value) return fallback;
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(value));
};

const getPayoutAvailableAt = (allocation) => {
    if (allocation?.payout_available_at) return allocation.payout_available_at;
    const dates = [];
    if (!allocation?.refund_window_closed_at && allocation?.refund_request_deadline) dates.push(allocation.refund_request_deadline);
    if (allocation?.transfer_release_at) dates.push(allocation.transfer_release_at);
    return dates.length ? dates.sort((first, second) => new Date(second) - new Date(first))[0] : null;
};

const getViews = (insights = {}) => metricNumber(insights.views ?? insights.plays);

const getInteractionTotal = (insights = {}) => (
    metricNumber(insights.total_interactions)
    || metricNumber(insights.likes) + metricNumber(insights.comments) + metricNumber(insights.saves) + metricNumber(insights.shares)
);

const comparePerformance = (a, b) => {
    const aInsights = a.insights || {};
    const bInsights = b.insights || {};
    return (
        getViews(bInsights) - getViews(aInsights)
        || metricNumber(bInsights.reach) - metricNumber(aInsights.reach)
        || getInteractionTotal(bInsights) - getInteractionTotal(aInsights)
        || metricNumber(bInsights.engagement_rate) - metricNumber(aInsights.engagement_rate)
    );
};

const creatorKey = (username = '') => username.replace('@', '').toLowerCase();

const summarizePosts = (posts = []) => posts.reduce((acc, post) => {
    const insights = post.insights || {};
    acc.views += getViews(insights);
    acc.reach = acc.reach === null || insights.reach === null || insights.reach === undefined
        ? null
        : acc.reach + metricNumber(insights.reach);
    acc.likes += metricNumber(insights.likes);
    acc.comments += metricNumber(insights.comments);
    acc.saves += metricNumber(insights.saves);
    acc.engagementTotal += metricNumber(insights.engagement_rate);
    acc.lastDeliveredAt = !acc.lastDeliveredAt || new Date(post.created_at) > new Date(acc.lastDeliveredAt)
        ? post.created_at
        : acc.lastDeliveredAt;
    return acc;
}, {
    views: 0,
    reach: 0,
    likes: 0,
    comments: 0,
    saves: 0,
    engagementTotal: 0,
    lastDeliveredAt: null,
});

const MetricsGrid = ({ metrics, dark = false }) => {
    const { t, i18n } = useTranslation('collab');
    const locale = i18n.resolvedLanguage || i18n.language || 'en';
    const metricItems = [
        {
            icon: <Eye size={14} />, value: metrics?.views, key: 'views',
            label: t('summary.performance.dashboard.metrics.views'),
            description: t('summary.performance.dashboard.explainers.views.description', 'Reproducciones reportadas. Si Meta no entrega views, se usa plays y luego impresiones como respaldo.'),
            formula: t('summary.performance.dashboard.explainers.views.formula', 'Vistas por pieza = views → plays → impressions'),
        },
        {
            icon: <Users size={14} />, value: metrics?.reach, key: 'reach',
            label: t('summary.performance.dashboard.metrics.reach'),
            description: t('summary.performance.dashboard.explainers.reach.description', 'Cuentas únicas alcanzadas por el contenido, según los Insights de Instagram/Meta.'),
            formula: t('summary.performance.dashboard.explainers.reach.formula', 'Reach total = suma del reach por pieza'),
        },
        {
            icon: <Heart size={14} />, value: metrics?.likes, key: 'likes',
            label: t('summary.performance.dashboard.metrics.likes'),
            description: t('summary.performance.dashboard.explainers.likes.description', 'Likes reportados por Instagram/Meta para las piezas incluidas.'),
            formula: t('summary.performance.dashboard.explainers.likes.formula', 'Likes totales = suma de likes por pieza'),
        },
        {
            icon: <MessageCircle size={14} />, value: metrics?.comments, key: 'comments',
            label: t('summary.performance.dashboard.metrics.comments'),
            description: t('summary.performance.dashboard.explainers.comments.description', 'Comentarios reportados por Instagram/Meta para las piezas incluidas.'),
            formula: t('summary.performance.dashboard.explainers.comments.formula', 'Comentarios totales = suma de comentarios por pieza'),
        },
        {
            icon: <Bookmark size={14} />, value: metrics?.saves, key: 'saves',
            label: t('summary.performance.dashboard.metrics.saves'),
            description: t('summary.performance.dashboard.explainers.saves.description', 'Guardados reportados por Instagram/Meta para las piezas incluidas.'),
            formula: t('summary.performance.dashboard.explainers.saves.formula', 'Guardados totales = suma de guardados por pieza'),
        },
    ];
    return (
        <div className={clsx(styles['metrics-grid'], dark && styles.dark)}>
            {metricItems.map((item, index) => (
                <div key={index} className={styles['mg-item']}>
                    <div className={styles['mg-icon']}>{item.icon}</div>
                    <div className={styles['mg-value']}>{formatNumber(item.value, locale)}</div>
                    <div className={styles['mg-label']}>
                        <span>{item.label}</span>
                        <MetricInfoPopover
                            title={item.label}
                            description={item.description}
                            formula={item.formula}
                            source={t('summary.performance.dashboard.explainers.source', 'Fuente: Insights certificados de Instagram/Meta.')}
                            label={`Ver explicación de ${item.label}`}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

const CreatorAvatar = ({ src, username, className }) => {
    const [failed, setFailed] = useState(false);
    const initial = username?.slice(0, 1)?.toUpperCase() || '?';

    return (
        <div className={className}>
            {src && !failed ? (
                <img src={src} alt="" onError={() => setFailed(true)} />
            ) : (
                <span>{initial}</span>
            )}
        </div>
    );
};

const BusinessMetricsDashboard = ({ offerId, title = null, offer = null, onApplicationCompleted = null }) => {
    const { t, i18n } = useTranslation('collab');
    const locale = i18n.resolvedLanguage || i18n.language || 'en';
    const [performanceData, setPerformanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedCreator, setExpandedCreator] = useState(null);
    const [creatorApplications, setCreatorApplications] = useState({});
    const [manualRefreshing, setManualRefreshing] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [submittingComplete, setSubmittingComplete] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {

                const [metricsRes, appsRes] = await Promise.all([
                    browserFetch(`/api/collabs/collab/${offerId}/metrics/`),
                    browserFetch(`/api/feed/applications/?offer=${offerId}`)
                ]);

                if (metricsRes.ok) setPerformanceData(await metricsRes.json());
                if (appsRes.ok) {
                    const apps = await appsRes.json();
                    const mapping = {};
                    (apps.results || apps).forEach(app => {
                        if (app.creator_username) {
                            mapping[creatorKey(app.creator_username)] = {
                                id: app.id,
                                status: app.status,
                                completedAt: app.completed_at,
                                paymentAllocation: app.payment_allocation,
                            };
                        }
                    });
                    setCreatorApplications(mapping);
                }
            } finally { setLoading(false); }
        };
        fetchAll();
    }, [offerId]);

    const totals = useMemo(() => {
        if (!performanceData?.posts) return { views: 0, reach: 0, likes: 0, comments: 0, saves: 0 };
        const exactTotalViews = performanceData.posts.reduce((total, post) => {
            const insights = post.insights || {};
            return insights.is_simulated ? total : total + getViews(insights);
        }, 0);
        return performanceData.campaign_level_totals && Object.keys(performanceData.campaign_level_totals).length > 0
            ? { ...performanceData.campaign_level_totals, views: exactTotalViews }
            : performanceData.posts.reduce((acc, post) => {
                const ins = post.insights || {};
                if (ins.is_simulated) return acc;
                acc.views += getViews(ins);
                acc.reach = acc.reach === null || ins.reach === null || ins.reach === undefined
                    ? null
                    : acc.reach + metricNumber(ins.reach);
                acc.likes += ins.likes || 0;
                acc.comments += ins.comments || 0;
                acc.saves += ins.saves || 0;
                return acc;
            }, { views: 0, reach: 0, likes: 0, comments: 0, saves: 0 });
    }, [performanceData]);

    const topPost = useMemo(() => {
        if (!performanceData?.posts?.length) return null;
        return [...performanceData.posts].sort(comparePerformance)[0];
    }, [performanceData]);

    const postsByCreator = useMemo(() => {
        if (!performanceData?.posts) return {};
        return (performanceData.posts || []).reduce((acc, post) => {
            if (!post?.creator?.username) return acc;
            const username = post.creator.username;
            if (!acc[username]) acc[username] = { creatorInfo: post.creator, posts: [] };
            acc[username].posts.push(post);
            return acc;
        }, {});
    }, [performanceData]);

    const creatorSummaries = useMemo(() => {
        return Object.entries(postsByCreator).map(([username, data]) => {
            const metrics = summarizePosts(data.posts);
            const avgEngagement = data.posts.length ? metrics.engagementTotal / data.posts.length : 0;
            const latestPost = [...data.posts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

            return {
                username,
                creatorInfo: data.creatorInfo,
                posts: data.posts,
                latestPost,
                metrics,
                avgEngagement,
            };
        }).sort((a, b) => b.metrics.views - a.metrics.views);
    }, [postsByCreator]);

    const handleConfirmComplete = async () => {
        if (!confirmTarget) return;
        const { username, app } = confirmTarget;
        if (!app?.id) { toast.error(t('summary.performance.dashboard.messages.application_missing')); return; }
        if (app.status === 'completed') { setConfirmTarget(null); return; }

        try {
            setSubmittingComplete(true);
            const res = await browserFetch(`/api/feed/applications/${app.id}/complete/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            if (res.ok) {
                const payload = await res.json();
                const updatedApp = payload.application || {};
                setCreatorApplications(prev => ({
                    ...prev,
                    [creatorKey(username)]: {
                        ...prev[creatorKey(username)],
                        status: 'completed',
                        completedAt: updatedApp.completed_at || new Date().toISOString(),
                        paymentAllocation: updatedApp.payment_allocation || prev[creatorKey(username)]?.paymentAllocation,
                    },
                }));
                toast.success(t('summary.performance.dashboard.messages.complete_success'));
                setConfirmTarget(null);
                if (typeof onApplicationCompleted === 'function') {
                    onApplicationCompleted(updatedApp);
                }
            } else {
                toast.error(t('summary.performance.dashboard.messages.complete_error'));
            }
        } catch (e) {
            toast.error(t('summary.performance.dashboard.messages.complete_error'));
        } finally {
            setSubmittingComplete(false);
        }
    };

    const handleManualRefresh = async () => {
        try {
            setManualRefreshing(true);
            const response = await refreshLatestCollabMetrics(offerId, 25);
            setPerformanceData(prev => prev ? { ...prev, freshness: response.data.freshness } : prev);
            toast.success(t('summary.performance.dashboard.messages.refresh_queued'));
        } catch (error) {
            toast.error(t('summary.performance.dashboard.messages.refresh_error'));
        } finally {
            setManualRefreshing(false);
        }
    };

    if (loading) return (
        <div className={styles['metrics-loading']}>
            <Loader2 className="animate-spin" size={32} />
            <p>{t('summary.performance.dashboard.loading')}</p>
        </div>
    );

    if (!performanceData?.posts?.length) return (
        <div className={styles['metrics-empty']}>
            <div className={styles['e-box']}><BarChart3 size={32} /></div>
            <h3>{t('summary.performance.dashboard.empty_title')}</h3>
            <p>{t('summary.performance.dashboard.empty_body')}</p>
        </div>
    );

    const freshness = performanceData?.freshness;
    const refreshPolicy = performanceData?.refresh_policy;

    return (
        <motion.div className={styles['metrics-workspace']} variants={containerVariants} initial="hidden" animate="visible">
            {freshness && (
                <motion.div className={styles['freshness-banner']} variants={itemVariants}>
                    <div className={styles['freshness-title']}>
                        <AlertTriangle size={16} />
                        {t('summary.performance.dashboard.freshness.title')}
                    </div>
                    <p>{t('summary.performance.dashboard.freshness.helper')}</p>
                    <div className={styles['freshness-stats']}>
                        <span><strong>{freshness.fresh}</strong> {t('summary.performance.dashboard.freshness.fresh')}</span>
                        <span><strong>{freshness.pending}</strong> {t('summary.performance.dashboard.freshness.pending')}</span>
                        <span><strong>{freshness.frozen}</strong> {t('summary.performance.dashboard.freshness.historic')}</span>
                        <span><strong>{freshness.error}</strong> {t('summary.performance.dashboard.freshness.retrying')}</span>
                    </div>
                    {refreshPolicy && (
                        <small>
                            {refreshPolicy.stories} {refreshPolicy.posts}
                        </small>
                    )}
                    <div className={styles['freshness-actions']}>
                        <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={manualRefreshing}>
                            <RefreshCw size={14} className={manualRefreshing ? 'animate-spin' : ''} />
                            {manualRefreshing
                                ? t('summary.performance.dashboard.freshness.queueing')
                                : t('summary.performance.dashboard.freshness.refresh')}
                        </Button>
                        <span>{t('summary.performance.dashboard.freshness.refresh_note')}</span>
                    </div>
                </motion.div>
            )}

            {/* HER0 TOTALS */}
            <motion.div className={styles['metrics-section']} variants={itemVariants}>
                <div className={styles['sec-head']}>
                    <div className={styles['s-icon']}><BarChart3 size={14} /></div>
                    <h5>{t('summary.performance.dashboard.total_impact')}</h5>
                </div>
                <Card className={styles['totals-card-premium']}>
                    <CardContent className="p-8">
                        <MetricsGrid metrics={totals} />
                    </CardContent>
                </Card>
            </motion.div>

            {/* TOP PERFORMER */}
            {topPost && (
                <motion.div className={styles['metrics-section']} variants={itemVariants}>
                    <div className={styles['sec-head']}>
                        <div className={styles['s-icon']}><Trophy size={14} /></div>
                        <h5>{t('summary.performance.dashboard.top_performer')}</h5>
                    </div>
                    <Card className={styles['top-performer-editorial']}>
                        <CardContent className="p-0">
                            <div className={styles['tp-card-grid']}>
                                <div className={styles['tp-profile-row']}>
                                    <CreatorAvatar
                                        src={topPost.creator.profile_picture_url}
                                        username={topPost.creator.username}
                                        className={styles['avatar-box']}
                                    />
                                    <div className={styles['profile-meta']}>
                                        <div className={styles.handle}>@{topPost.creator.username}</div>
                                        <div className={styles.engagement}>{formatPercent(topPost.insights?.engagement_rate)} {t('summary.performance.dashboard.engagement_rate')}</div>
                                        <a href={normalizeUrl(topPost.post_url)} target="_blank" rel="noopener noreferrer" className={styles['post-link']}>
                                            {t('summary.performance.dashboard.view_deliverable')} <ExternalLink size={12} />
                                        </a>
                                    </div>
                                </div>
                                <div className={styles['tp-highlight']}>
                                    <span>{t('summary.performance.dashboard.top_content')}</span>
                                    <strong>{topPost.post_type || 'post'}</strong>
                                    <small>{formatDate(topPost.created_at, locale, t('summary.performance.dashboard.no_date'))}</small>
                                </div>
                                <div className={styles['badge-float']}>
                                    <Badge variant="success">{t('summary.performance.dashboard.top_performer')}</Badge>
                                </div>
                            </div>
                            <div className={styles['tp-metrics-wrap']}>
                                <MetricsGrid metrics={topPost.insights} dark />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* CREATOR BREAKDOWN */}
            <motion.div className={styles['metrics-section']} variants={itemVariants}>
                <div className={styles['sec-head']}>
                    <div className={styles['s-icon']}><Users size={14} /></div>
                    <h5>{t('summary.performance.dashboard.deliverables_by_creator')}</h5>
                </div>
                <div className={styles['creator-overview']}>
                    <div>
                        <strong>{creatorSummaries.length}</strong>
                        <span>{t(`summary.performance.dashboard.${creatorSummaries.length === 1 ? 'creator_with_delivery_one' : 'creator_with_delivery_many'}`)}</span>
                    </div>
                    <div>
                        <strong>{performanceData.posts.length}</strong>
                        <span>{t(`summary.performance.dashboard.${performanceData.posts.length === 1 ? 'deliverable_received_label_one' : 'deliverable_received_label_many'}`)}</span>
                    </div>
                    <div>
                        <strong>{formatNumber(totals.views, locale)}</strong>
                        <span>{t('summary.performance.dashboard.total_views')}</span>
                    </div>
                </div>
                <div className={styles['creator-list']}>
                    {creatorSummaries.map((creator) => {
                        const { username, creatorInfo, posts, latestPost, metrics, avgEngagement } = creator;
                        const open = expandedCreator === username;
                        const application = creatorApplications[creatorKey(username)];
                        const isCompleted = application?.status === 'completed';
                        const allocation = application?.paymentAllocation;
                        const isPaid = Boolean(offer?.compensation_mode && ['paid', 'hybrid'].includes(offer.compensation_mode)) || Boolean(offer?.sponsor_this_offer) || Boolean(allocation);
                        const isPaymentTransferred = allocation?.transfer_status === 'transferred';
                        const isPaymentScheduled = ['refund_window_open', 'scheduled', 'processing'].includes(allocation?.transfer_status) || ['manually_approved', 'automatically_approved'].includes(allocation?.approval_status);
                        const payoutAvailableAt = getPayoutAvailableAt(allocation);
                        const amountFormatted = allocation?.creator_amount
                            ? `$${allocation.creator_amount} ${allocation.currency || 'USD'}`
                            : (offer?.sponsor_amount ? `$${offer.sponsor_amount} USD` : null);

                        return (
                            <div key={username} className={clsx(styles['perf-block'], open && styles.open)}>
                                <div
                                    className={styles['block-header']}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={open}
                                    onClick={() => setExpandedCreator(open ? null : username)}
                                    onKeyDown={(event) => {
                                        if (event.target !== event.currentTarget) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            setExpandedCreator(open ? null : username);
                                        }
                                    }}
                                >
                                    <div className={styles['c-info']}>
                                        <CreatorAvatar
                                            src={creatorInfo.profile_picture_url}
                                            username={username}
                                            className={styles.avatar}
                                        />
                                        <div className={styles.txt}>
                                            <div className={styles.name}>@{username}</div>
                                            <span className={styles.count}>
                                                <CheckCircle2 size={13} />
                                                {t(`summary.performance.dashboard.${posts.length === 1 ? 'deliverable_received_one' : 'deliverable_received_many'}`, { count: posts.length })}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles['creator-snapshot']}>
                                        <div className={styles['snapshot-item']}>
                                            <span>{formatNumber(metrics.views, locale)}</span>
                                            <small>{t('summary.performance.dashboard.metrics.views')}</small>
                                        </div>
                                        <div className={styles['snapshot-item']}>
                                            <span>{formatNumber(metrics.reach, locale)}</span>
                                            <small>{t('summary.performance.dashboard.metrics.reach')}</small>
                                        </div>
                                        <div className={styles['snapshot-item']}>
                                            <span>{formatPercent(avgEngagement)}</span>
                                            <small>{t('summary.performance.dashboard.metrics.engagement_short')}</small>
                                        </div>
                                    </div>
                                    <div className={styles['creator-actions']}>
                                        {isCompleted ? (
                                            <div
                                                className={styles['completed-pill']}
                                                title={isPaid && isPaymentScheduled && payoutAvailableAt
                                                    ? t('summary.performance.dashboard.payment_scheduled_on', { date: formatDate(payoutAvailableAt, locale, t('summary.performance.dashboard.no_date')) })
                                                    : (application?.completedAt
                                                        ? t('summary.performance.dashboard.completed_on', { date: formatDate(application.completedAt, locale, t('summary.performance.dashboard.no_date')) })
                                                        : t('summary.performance.dashboard.completed'))}
                                            >
                                                <CheckCircle2 size={14} />
                                                {isPaid && isPaymentTransferred
                                                    ? t('summary.performance.dashboard.payment_released')
                                                    : (isPaid && isPaymentScheduled
                                                        ? t('summary.performance.dashboard.payment_scheduled')
                                                        : (isPaid
                                                            ? t('summary.performance.dashboard.delivery_approved')
                                                            : t('summary.performance.dashboard.completed')))}
                                            </div>
                                        ) : (
                                            <Button
                                                variant="primary"
                                                size="xs"
                                                className={isPaid ? styles['btn-approve-pay'] : undefined}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmTarget({
                                                        username,
                                                        app: application,
                                                        allocation,
                                                        isPaid,
                                                        amountFormatted,
                                                    });
                                                }}
                                            >
                                                {isPaid && amountFormatted
                                                    ? t('summary.performance.dashboard.approve_release_payment', { amount: amountFormatted })
                                                    : t('summary.performance.dashboard.approve_finish_delivery')}
                                            </Button>
                                        )}
                                        <motion.div className={styles['expand-icon']} animate={{ rotate: open ? 180 : 0 }}>
                                            <ChevronDown size={18} />
                                        </motion.div>
                                    </div>
                                </div>

                                <div className={styles['creator-strip']}>
                                    <div className={styles['strip-item']}>
                                        <Clock3 size={13} />
                                        <span>{t('summary.performance.dashboard.latest_delivery', { date: formatDate(latestPost?.created_at, locale, t('summary.performance.dashboard.no_date')) })}</span>
                                    </div>
                                    <div className={styles['delivery-types']}>
                                        {posts.map((post) => (
                                            <span key={`${post.id}-${post.post_type}`} className={styles['type-chip']}>
                                                {getPostTypeIcon(post.post_type)}
                                                {post.post_type || 'post'}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <AnimatePresence>
                                {open && (
                                    <motion.div
                                      className={styles['block-body']}
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                    >
                                        {posts.map((post, pIdx) => (
                                            <div key={pIdx} className={styles['post-entry']}>
                                                <div className={styles['post-meta-row']}>
                                                    <div className={styles['p-type']}>
                                                        {getPostTypeIcon(post.post_type)}
                                                        <span>{post.post_type}</span>
                                                    </div>
                                                    <span className={styles['p-date']}>{formatDate(post.created_at, locale, t('summary.performance.dashboard.no_date'))}</span>
                                                    <a href={normalizeUrl(post.post_url)} target="_blank" rel="noopener noreferrer" className={styles['p-url']}>
                                                        <Instagram size={12} /> {t('summary.performance.dashboard.open_instagram')}
                                                    </a>
                                                    {post.insights?.engagement_rate != null && (
                                                        <div className={styles['p-eng']}>{formatPercent(post.insights.engagement_rate)} Eng.</div>
                                                    )}
                                                    {post.insights_refresh_state && (
                                                        <div className={styles['p-refresh-state']}>
                                                            {post.insights_refresh_state === 'pending'
                                                                ? t('summary.performance.dashboard.states.processing')
                                                                : post.insights_refresh_state === 'frozen'
                                                                    ? t('summary.performance.dashboard.states.snapshot')
                                                                    : post.insights_refresh_state === 'error'
                                                                        ? t('summary.performance.dashboard.states.retrying')
                                                                        : t('summary.performance.dashboard.states.updated')}
                                                        </div>
                                                    )}
                                                </div>
                                                <MetricsGrid metrics={post.insights} />
                                            </div>
                                        ))}
                                    </motion.div>
                                )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </motion.div>

            {confirmTarget && (
                <div className={styles['confirm-modal-overlay']} onClick={() => !submittingComplete && setConfirmTarget(null)}>
                    <div className={styles['confirm-modal']} onClick={(e) => e.stopPropagation()}>
                        <div className={styles['modal-header']}>
                            <div className={styles['modal-icon']}>
                                <CheckCircle2 size={24} />
                            </div>
                            <h4>{t('summary.performance.dashboard.confirm_modal.title')}</h4>
                        </div>
                        <p className={styles['modal-body']}>
                            {confirmTarget.isPaid
                                ? t('summary.performance.dashboard.confirm_modal.body', { username: confirmTarget.username, amount: confirmTarget.amountFormatted })
                                : t('summary.performance.dashboard.confirm_modal.body_gifting', { username: confirmTarget.username })}
                        </p>
                        <div className={styles['modal-note']}>
                            {confirmTarget.isPaid
                                ? t('summary.performance.dashboard.confirm_modal.note')
                                : t('summary.performance.dashboard.confirm_modal.note_gifting')}
                        </div>
                        <div className={styles['modal-actions']}>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmTarget(null)} disabled={submittingComplete}>
                                {t('summary.performance.dashboard.confirm_modal.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={submittingComplete}
                                onClick={handleConfirmComplete}
                            >
                                {submittingComplete
                                    ? '...'
                                    : (confirmTarget.isPaid
                                        ? t('summary.performance.dashboard.confirm_modal.confirm_paid')
                                        : t('summary.performance.dashboard.confirm_modal.confirm_gifting'))}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default BusinessMetricsDashboard;
